import * as React from 'react';
import { animate, AnimatePresence, motion, useReducedMotion } from 'framer-motion';
import { ChevronRight, Info } from 'lucide-react';
import { twMerge } from 'tailwind-merge';
import { useNotifications, type NotificationAlertStep } from './notificationAlertStore';
import { useUser } from '../UserContext';
import { useSubMenu } from '../SubMenuContext';
import { useTranslation } from '../lib/i18n/TranslationContext';
import { EmpreenderApps } from '../lib/AppsArray';
import { getStatusApp } from '../lib/statusApp';
import type { CurrentStoreProps } from '../SideBar';

type NotificationAlertProps = {
	message?: React.ReactNode;
	actionLabel?: React.ReactNode;
	suffix?: React.ReactNode;
	href?: string;
	className?: string;
	icon?: React.ReactNode;
	showChevron?: boolean;
	onClick?: React.MouseEventHandler<HTMLElement>;
	target?: React.HTMLAttributeAnchorTarget;
	rel?: string;
	steps?: NotificationAlertStep[];
} & Omit<React.HTMLAttributes<HTMLDivElement>, 'children' | 'className' | 'onClick'>;

const slideVariants = {
	enter: (dir: number) => ({ x: dir > 0 ? '55%' : '-55%', opacity: 0 }),
	center: { x: 0, opacity: 1 },
	exit: (dir: number) => ({ x: dir > 0 ? '-55%' : '55%', opacity: 0 }),
};

const TROCA_AUTOMATICA = 7000;

const semMovimento = {
	enter: { opacity: 0 },
	center: { opacity: 1 },
	exit: { opacity: 0 },
};

function ChevronButton({ onClick, label = 'Próximo aviso' }: { onClick?: React.MouseEventHandler<HTMLButtonElement>; label?: string }) {
	return (
		<button
			type='button'
			onClick={onClick}
			aria-label={label}
			className='inline-flex size-4 shrink-0 items-center justify-center rounded-sm text-[#543406] outline-none transition-colors hover:bg-[#E6C84A]'>
			<ChevronRight className='size-3.5' strokeWidth={2.25} aria-hidden />
		</button>
	);
}

const RAIO_DO_CANTO = 20;
const RAIO_DA_PILULA = 8;
const LARGURA_DO_CANTO = RAIO_DO_CANTO + RAIO_DA_PILULA;

const CENTRO_CONVEXO = Math.sqrt(RAIO_DO_CANTO * RAIO_DO_CANTO + 2 * RAIO_DO_CANTO * RAIO_DA_PILULA);
const EMENDA_X = (RAIO_DO_CANTO * CENTRO_CONVEXO) / (RAIO_DO_CANTO + RAIO_DA_PILULA);
const EMENDA_Y = RAIO_DO_CANTO - (RAIO_DO_CANTO * RAIO_DA_PILULA) / (RAIO_DO_CANTO + RAIO_DA_PILULA);

const CURVA_DO_CANTO = [
	'M0,0',
	`A${RAIO_DO_CANTO},${RAIO_DO_CANTO} 0 0 1 ${EMENDA_X.toFixed(2)},${EMENDA_Y.toFixed(2)}`,
	`A${RAIO_DA_PILULA},${RAIO_DA_PILULA} 0 0 0 ${CENTRO_CONVEXO.toFixed(2)},${RAIO_DO_CANTO}`,
	`L${LARGURA_DO_CANTO},${RAIO_DO_CANTO}`,
	`L${LARGURA_DO_CANTO},0`,
	'Z',
].join(' ');

function CantoDaFaixa({ lado }: { lado: 'esquerda' | 'direita' }) {
	return (
		<svg
			aria-hidden
			width={LARGURA_DO_CANTO}
			height={RAIO_DO_CANTO}
			viewBox={`0 0 ${LARGURA_DO_CANTO} ${RAIO_DO_CANTO}`}
			className={twMerge(
				'pointer-events-none absolute top-1 text-[#FFE87A]',
				lado === 'esquerda' ? 'right-full translate-x-[8px]' : 'left-full -translate-x-[8px] -scale-x-100'
			)}>
			<path d={CURVA_DO_CANTO} fill='currentColor' />
		</svg>
	);
}

function hasContent(node: React.ReactNode) {
	if (node == null || node === false) return false;
	if (typeof node === 'string') return node.trim().length > 0;
	return true;
}

function StepContent({ step, fallbackActionLabel, fallbackSuffix }: { step: NotificationAlertStep; fallbackActionLabel?: React.ReactNode; fallbackSuffix?: React.ReactNode }) {
	const label = step.actionLabel ?? fallbackActionLabel;
	const suffix = step.suffix ?? fallbackSuffix;

	return (
		<span className='min-w-0 flex-1 [&_a]:underline [&_a]:underline-offset-[3px]'>
			{step.message}
			{hasContent(label) && (
				<>
					{' '}
					<span className='underline underline-offset-[3px]'>{label}</span>
				</>
			)}
			{hasContent(suffix) && suffix}
		</span>
	);
}

function StepAction({ step, children }: { step: NotificationAlertStep; children: React.ReactNode }) {
	const interactive = Boolean(step.href || step.onClick);
	const actionClassName = twMerge('relative inline-flex min-w-0 flex-1 items-center gap-3 text-left', interactive && 'cursor-pointer transition-opacity hover:opacity-90');

	if (step.href) {
		return (
			<a
				href={step.href}
				target={step.target}
				rel={step.rel ?? (step.target === '_blank' ? 'noopener noreferrer' : undefined)}
				className={actionClassName}
				onClick={step.onClick}>
				{children}
			</a>
		);
	}

	if (step.onClick) {
		return (
			<button type='button' className={actionClassName} onClick={step.onClick}>
				{children}
			</button>
		);
	}

	return <div className={actionClassName}>{children}</div>;
}

function NotificationAlert({ message, actionLabel, suffix, href, className, icon, showChevron = true, onClick, target, rel, steps, ...props }: NotificationAlertProps) {
	const [activeIndex, setActiveIndex] = React.useState(0);
	const [direction, setDirection] = React.useState(1);
	const reduzirMovimento = useReducedMotion();

	const resolvedSteps: NotificationAlertStep[] = steps?.length ? steps : [{ message, actionLabel, suffix, href, icon, onClick, target, rel }];

	const total = resolvedSteps.length;
	const isMultiStep = total > 1;

	const totalAnterior = React.useRef(total);
	React.useEffect(() => {
		if (total > totalAnterior.current) {
			setDirection(1);
			setActiveIndex(total - 1);
		} else {
			setActiveIndex((prev) => Math.min(prev, total - 1));
		}

		totalAnterior.current = total;
	}, [total]);

	const currentStep = resolvedSteps[Math.min(activeIndex, total - 1)];

	const avancar = React.useCallback(() => {
		setDirection(1);
		setActiveIndex((prev) => (prev + 1) % total);
	}, [total]);

	const handleNext: React.MouseEventHandler<HTMLButtonElement> = (e) => {
		e.preventDefault();
		e.stopPropagation();
		avancar();
	};

	const [pausado, setPausado] = React.useState(false);

	React.useEffect(() => {
		if (!isMultiStep || pausado) return;

		const relogio = setTimeout(avancar, TROCA_AUTOMATICA);

		return () => clearTimeout(relogio);
	}, [isMultiStep, pausado, activeIndex, avancar]);

	const pillClassName = twMerge(
		'relative z-[1] inline-flex max-w-full items-center gap-3 rounded-b-lg bg-[#FFE87A] px-3 py-1 text-left text-xs font-medium leading-none text-neutral-950',
		className
	);

	const molaDoTexto = reduzirMovimento ? { duration: 0.12 } : { x: { type: 'spring' as const, bounce: 0, duration: 0.4 }, opacity: { duration: 0.18, ease: 'easeOut' as const } };

	const pilula = React.useRef<HTMLDivElement>(null);
	const larguraAssentada = React.useRef<number | null>(null);

	React.useLayoutEffect(() => {
		const caixa = pilula.current;
		if (!caixa) return;

		const texto = caixa.querySelector<HTMLElement>('[data-slot="notification-alert-texto"]');
		const travada = caixa.style.width;
		const partida = travada ? parseFloat(travada) : larguraAssentada.current;

		const soltar = () => {
			caixa.style.width = '';
			if (texto) texto.style.whiteSpace = '';
		};

		soltar();
		const alvo = caixa.getBoundingClientRect().width;
		larguraAssentada.current = alvo;

		if (partida == null || reduzirMovimento || Math.abs(alvo - partida) < 1) return;

		if (texto) texto.style.whiteSpace = 'nowrap';
		caixa.style.width = `${partida}px`;

		const largura = animate(partida, alvo, {
			type: 'spring',
			bounce: 0,
			duration: 0.4,
			onUpdate: (valor) => {
				caixa.style.width = `${valor}px`;
			},
			onComplete: soltar,
		});

		return () => largura.stop();
	}, [activeIndex, total, reduzirMovimento]);

	return (
		<div data-slot='notification-alert' className='relative flex w-full justify-center'>
			<div className='pointer-events-none absolute inset-x-0 top-0 h-1 bg-[#FFE87A]' aria-hidden />
			<div
				ref={pilula}
				role='status'
				onMouseEnter={() => setPausado(true)}
				onMouseLeave={() => setPausado(false)}
				onFocusCapture={() => setPausado(true)}
				onBlurCapture={() => setPausado(false)}
				className={pillClassName}
				{...props}>
				<CantoDaFaixa lado='esquerda' />
				<CantoDaFaixa lado='direita' />
				<span data-slot='notification-alert-icone' className='inline-flex text-[#543406] size-3.5 shrink-0 items-center justify-center' aria-hidden>
					{currentStep.icon ?? <Info className='size-3.5' strokeWidth={2.25} />}
				</span>
				<span data-slot='notification-alert-texto' className='relative flex min-w-0 flex-1 items-center self-stretch overflow-hidden'>
					<AnimatePresence mode='popLayout' initial={false} custom={direction}>
						<motion.span
							key={activeIndex}
							custom={direction}
							variants={reduzirMovimento ? semMovimento : slideVariants}
							initial='enter'
							animate='center'
							exit='exit'
							transition={molaDoTexto}
							className='inline-flex min-w-0 flex-1 items-center text-[#543406] font-sans text-[12px] font-medium leading-[16px] tracking-[-0.4px] decoration-solid'>
							<StepAction step={currentStep}>
								<StepContent step={currentStep} fallbackActionLabel={actionLabel} fallbackSuffix={suffix} />
							</StepAction>
						</motion.span>
					</AnimatePresence>
				</span>
				{showChevron && isMultiStep ? <ChevronButton onClick={handleNext} /> : null}
			</div>
		</div>
	);
}

const linkDoPlano = (current_store: CurrentStoreProps, app: string) => {
	const matchApp = EmpreenderApps.find((a) => a.tags.some((tag) => tag?.toLowerCase() === app?.toLowerCase()));

	return `https://app.empreender.plus/planos?app=${matchApp?.eplus}&p=base&chave=${current_store.chave_empreender}`;
};

export const passosDoPlano = (current_store: CurrentStoreProps, app: string, t: (key: string) => string): NotificationAlertStep[] => {
	const status = getStatusApp(current_store);

	if (status === 'ativo') return [];

	const passo = {
		href: linkDoPlano(current_store, app),
		target: '_blank' as const,
		actionLabel: t('Ative agora'),
		suffix: ' ' + t('e continue usando todos os recursos.'),
	};

	if (status === 'expired') return [{ ...passo, message: t('Seu plano expirou e os recursos estão limitados.') }];

	const dias = current_store.dias_restantes_trial ?? 0;
	const restam = dias <= 1 ? t('Resta 1 dia de avaliação gratuita.') : t('Restam {dias} dias de avaliação gratuita.').replace('{dias}', String(dias));

	return [{ ...passo, message: restam }];
};

function NotificationAlertAuto({ className }: { className?: string }) {
	const { current_store } = useUser()?.user || {};
	const { app } = useSubMenu();
	const { t } = useTranslation();
	const avisos = useNotifications();

	const automaticos = React.useMemo(() => (current_store ? passosDoPlano(current_store, app, t) : []), [current_store, app, t]);

	const steps = [...automaticos, ...avisos];

	if (!steps.length) return null;

	return (
		<div data-slot='notification-alert-auto' className={twMerge('pointer-events-none absolute inset-x-0 top-0 z-10 overflow-hidden', className)}>
			<NotificationAlert steps={steps} className='pointer-events-auto' />
		</div>
	);
}

export { NotificationAlert, NotificationAlertAuto };
export { addNotification, removeNotification, useNotifications } from './notificationAlertStore';
export type { NotificationAlertProps };
export type { NotificationAlertStep, NotificationAlertInput } from './notificationAlertStore';
